import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listAzpneu, getAzpneuStats, schvalitAzpneu, skrytAzpneu, nastavCenuAzpneu,
  type AzpneuDezen, type AzpneuRozmer,
} from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { AlertIcon } from "../lib/icons.js";

/**
 * AZ PNEU — moto pneumatiky (Michelin, Mitas) z velkoobchodu az-pneu.
 *
 * 🔑 JEDEN DEZÉN = JEDEN PRODUKT na eshopu, rozměry jsou varianty. Proto je
 * seznam seskupený po dezénech, ale schvaluje se po ROZMĚRECH — dodavatel
 * je má skladem každý zvlášť.
 *
 * 💰 Tři čísla, kvůli kterým to celé je (Vít 17. 8. 2026):
 *     nákupka (VOC bez DPH) · za kolik prodává dodavatel · NAŠE cena
 * Naše se počítá jako (VOC + doprava 85,12 Kč/ks) × marže × DPH a jde
 * PŘEPSAT ručně — *„navrhovanou cenu si případně na základě dat můžu změnit."*
 *
 * ⛔⛔ Ceníkovou MOC (přeškrtnutá cena u dodavatele) tu záměrně NEUKAZUJEME.
 * Je to marketingová kotva — dodavatel sám prodává v mediánu za 52 % z ní —
 * a hlavně ji nesmíme převzít jako naši původní cenu (ČOI).
 *
 * ⚠ Sloupec „vs dodavatel" je HLÍDAČ. Když jsme o hodně výš, bývá to chyba
 * v jeho ceníku, ne v našem vzorci: Mitas Terra Force-MX Sand 2 vyšel +51 %,
 * protože ho dodavatel prodává pod vlastní velkoobchodní cenou.
 */

const KATEGORIE = [
  { id: "motokros", label: "Motokros" },
  { id: "enduro", label: "Enduro" },
  { id: "cross-country", label: "Cross country" },
  { id: "extreme-enduro", label: "Extreme enduro" },
  { id: "dual-sport", label: "Dual sport" },
  { id: "adventure", label: "Adventure" },
  { id: "rally", label: "Rally" },
  { id: "supermoto", label: "Supermoto" },
  { id: "street", label: "Street" },
] as const;

const kc = (v: string | null): string =>
  v === null ? "—" : `${Math.round(Number(v)).toLocaleString("cs-CZ")} Kč`;

/** Sklad u dodavatele jednou větou — „Opava 8 ks" je jiná liga než „do 5 dní". */
function Sklad({ s }: { s: AzpneuRozmer["sklad"] }): JSX.Element {
  if (s.celkem === 0) return <span className="text-red-600">nemá</span>;
  if (s.opava > 0) return <span className="text-green-700">Opava {s.opava} ks</span>;
  if (s.do24h + s.do48h > 0) return <span className="text-green-600">do 48 h ({s.do24h + s.do48h})</span>;
  return <span className="text-amber-600">2–5 dní ({s.do2az5d})</span>;
}

function StavBadge({ stav }: { stav: AzpneuRozmer["stav"] }): JSX.Element | null {
  if (stav === "na-eshopu") return <Badge tone="green">Na eshopu</Badge>;
  if (stav === "ceka") return <Badge tone="blue">Čeká na robota</Badge>;
  if (stav === "skryto") return <Badge tone="neutral">Skryto</Badge>;
  if (stav === "chyba") return <Badge tone="red">Chyba</Badge>;
  return null;
}

function RadekRozmeru({
  r, onZmena,
}: { r: AzpneuRozmer; onZmena: () => void }): JSX.Element {
  const [edituji, setEdituji] = useState(false);
  const [hodnota, setHodnota] = useState(r.naseCena ? String(Math.round(Number(r.naseCena))) : "");
  const [chyba, setChyba] = useState<string | null>(null);
  const [pracuje, setPracuje] = useState(false);

  const uloz = async (): Promise<void> => {
    setPracuje(true); setChyba(null);
    try {
      await nastavCenuAzpneu(r.azpneuId, hodnota.trim() === "" ? null : Number(hodnota));
      setEdituji(false); onZmena();
    } catch (e) { setChyba(e instanceof Error ? e.message : "nepovedlo se"); }
    finally { setPracuje(false); }
  };

  const akce = async (fn: (id: string) => Promise<void>): Promise<void> => {
    setPracuje(true); setChyba(null);
    try { await fn(r.azpneuId); onZmena(); }
    catch (e) { setChyba(e instanceof Error ? e.message : "nepovedlo se"); }
    finally { setPracuje(false); }
  };

  // Hlídač: nad 20 % nad dodavatelem je většinou chyba v JEHO ceníku.
  const odchylka = r.odchylkaProtiDodavateli;
  const podezrele = odchylka !== null && odchylka > 0.2;

  return (
    <tr className="border-t border-slate-100 text-sm">
      <td className="py-2 pr-3 font-medium whitespace-nowrap">
        {r.sizeLabel}{" "}
        {r.position && (
          <span className="text-slate-400">{r.position === "P" ? "přední" : r.position === "Z" ? "zadní" : ""}</span>
        )}
      </td>
      <td className="py-2 pr-3 text-slate-400 font-mono text-xs">{r.manufacturerCode ?? "—"}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{kc(r.nakupka)}</td>
      <td className="py-2 pr-3 text-right tabular-nums text-slate-500">{kc(r.dodavatelProdava)}</td>
      <td className="py-2 pr-3 text-right tabular-nums font-semibold">
        {edituji ? (
          <span className="inline-flex items-center gap-1">
            <input
              type="number" value={hodnota} onChange={(e) => setHodnota(e.target.value)}
              className="w-24 rounded border border-slate-300 px-2 py-1 text-right"
              placeholder="prázdné = návrh"
            />
            <Button size="sm" onClick={uloz} disabled={pracuje}>Ulož</Button>
            <Button size="sm" variant="ghost" onClick={() => setEdituji(false)}>×</Button>
          </span>
        ) : (
          <button
            type="button" onClick={() => setEdituji(true)}
            className="hover:underline decoration-dotted"
            title={r.cenaRucne ? `Ručně přepsáno (návrh byl ${kc(r.naseCenaNavrzena)})` : "Klikni pro ruční cenu"}
          >
            {kc(r.naseCena)}
            {r.cenaRucne && <span className="ml-1 text-amber-600" title="ruční cena">✎</span>}
          </button>
        )}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">
        {odchylka === null ? "—" : (
          <span className={podezrele ? "text-amber-700 font-medium" : "text-slate-400"}>
            {podezrele && <AlertIcon size={12} className="inline mr-0.5" />}
            {(odchylka * 100 > 0 ? "+" : "")}{Math.round(odchylka * 100)} %
          </span>
        )}
      </td>
      <td className="py-2 pr-3 whitespace-nowrap"><Sklad s={r.sklad} /></td>
      <td className="py-2 pr-3"><StavBadge stav={r.stav} /></td>
      <td className="py-2 text-right whitespace-nowrap">
        {r.stav === "na-eshopu" || r.stav === "ceka" ? (
          <Button size="sm" variant="ghost" disabled={pracuje} onClick={() => akce(skrytAzpneu)}>
            Skrýt
          </Button>
        ) : (
          <Button size="sm" disabled={pracuje || r.sklad.celkem === 0} onClick={() => akce(schvalitAzpneu)}>
            Na eshop
          </Button>
        )}
        {chyba && <div className="text-xs text-red-600 mt-1 max-w-[16rem] text-right">{chyba}</div>}
      </td>
    </tr>
  );
}

export function AzpneuPage(): JSX.Element {
  const [dezeny, setDezeny] = useState<AzpneuDezen[] | null>(null);
  const [stats, setStats] = useState<Awaited<ReturnType<typeof getAzpneuStats>> | null>(null);
  const [kategorie, setKategorie] = useState<string | null>("motokros");
  const [znacka, setZnacka] = useState<string | null>(null);
  const [jenSkladem, setJenSkladem] = useState(true);
  const [hledat, setHledat] = useState("");
  const [otevrene, setOtevrene] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const nacti = useCallback(async () => {
    setLoading(true);
    try {
      const dotaz: Parameters<typeof listAzpneu>[0] = { jenSkladem };
      if (kategorie) dotaz.kategorie = kategorie;
      if (znacka) dotaz.znacka = znacka;
      if (hledat.trim()) dotaz.hledat = hledat.trim();
      const r = await listAzpneu(dotaz);
      setDezeny(r.dezeny);
    } finally { setLoading(false); }
  }, [kategorie, znacka, jenSkladem, hledat]);

  useEffect(() => { void nacti(); }, [nacti]);
  useEffect(() => { void getAzpneuStats().then(setStats).catch(() => setStats(null)); }, []);

  const prepni = (klic: string): void => {
    setOtevrene((p) => {
      const n = new Set(p);
      if (n.has(klic)) n.delete(klic); else n.add(klic);
      return n;
    });
  };

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-xl font-semibold">AZ PNEU — moto pneumatiky</h1>
        <p className="text-sm text-slate-500 mt-1">
          Jeden dezén = jeden produkt na eshopu, rozměry jsou varianty. Cena se počítá
          z nákupky + dopravy 85 Kč/ks + marže — klikni na ni a přepiš ji ručně.
        </p>
      </header>

      {stats && (
        <div className="flex flex-wrap gap-4 text-sm text-slate-600">
          <span>Na eshopu: <strong>{stats.naEshopu}</strong></span>
          <span>Čeká na robota: <strong>{stats.ceka}</strong></span>
          {stats.bezKodu > 0 && (
            <span className="text-amber-700">Bez kódu výrobce: <strong>{stats.bezKodu}</strong></span>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {KATEGORIE.map((k) => (
          <Button key={k.id} size="sm" variant={kategorie === k.id ? "primary" : "ghost"}
            onClick={() => setKategorie(kategorie === k.id ? null : k.id)}>
            {k.label}
            {stats && (
              <span className="ml-1 opacity-60">
                {stats.kategorie.find((x) => x.kategorie === k.id)?.pocet ?? 0}
              </span>
            )}
          </Button>
        ))}
        <span className="mx-2 h-5 w-px bg-slate-200" />
        {["Michelin", "Mitas"].map((z) => (
          <Button key={z} size="sm" variant={znacka === z ? "primary" : "ghost"}
            onClick={() => setZnacka(znacka === z ? null : z)}>{z}</Button>
        ))}
        <label className="ml-2 flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={jenSkladem} onChange={(e) => setJenSkladem(e.target.checked)} />
          jen skladem u dodavatele
        </label>
        <input
          value={hledat} onChange={(e) => setHledat(e.target.value)}
          placeholder="dezén, rozměr, kód výrobce…"
          className="ml-auto w-64 rounded border border-slate-300 px-3 py-1.5 text-sm"
        />
      </div>

      {loading && !dezeny ? <LoadingBlock /> : null}
      {dezeny && dezeny.length === 0 ? (
        <EmptyState title="Nic tu není" description="Zkus jiný filtr, nebo ještě neproběhl sběr z velkoobchodu." />
      ) : null}

      {/* Mřížka karet — jedna karta = jeden DEZÉN = jeden budoucí produkt na eshopu. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {dezeny?.map((d) => {
          const klic = `${d.brand}|${d.pattern}`;
          const vybrano = otevrene.has(klic);
          const vse = d.naEshopu === d.pocetRozmeru && d.pocetRozmeru > 0;
          return (
            <Link
              key={klic}
              to={`/azpneu/${encodeURIComponent(d.brand)}/${encodeURIComponent(d.pattern)}`}
              className="group block text-left bg-white rounded-xl border border-zinc-200 overflow-hidden transition-all hover:border-ktm-orange hover:shadow-md"
            >
              <div className="aspect-[4/3] bg-zinc-50 relative overflow-hidden">
                {d.imgUrl ? (
                  <img src={d.imgUrl} alt={d.pattern}
                       className="w-full h-full object-contain p-2 group-hover:scale-[1.03] transition-transform" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-zinc-300 text-xs">bez fotky</div>
                )}
                <div className="absolute bottom-1.5 left-1.5 flex gap-1 flex-wrap">
                  {/* „Vedeme" u pneu = je to na eshopu. Dodavatel to má vždycky. */}
                  {d.naEshopu === 0 && <Badge tone="amber">Nevedeme</Badge>}
                  {d.naEshopu > 0 && !vse && <Badge tone="blue">{d.naEshopu} z {d.pocetRozmeru} na eshopu</Badge>}
                  {vse && <Badge tone="green">Na eshopu</Badge>}
                  {d.skladCelkem === 0 && <Badge tone="neutral">Dodavatel nemá</Badge>}
                </div>
                <div className="absolute top-1.5 right-1.5 rounded bg-white/90 border border-zinc-200 px-1 py-0.5 text-[10px] text-zinc-600">
                  {d.pocetRozmeru}× rozměr
                </div>
              </div>

              <div className="p-2.5">
                <div className="text-[11px] text-zinc-400 truncate">{d.brand}{d.kategorie ? ` · ${d.kategorie}` : ""}</div>
                <div className="mt-0.5 text-[13px] font-medium text-zinc-900 line-clamp-2 min-h-[2.2rem] leading-snug">
                  {d.pattern}
                </div>
                <div className="mt-1.5 text-[13px] font-semibold text-zinc-900">
                  {d.cenaOd === null ? "—"
                    : d.cenaOd === d.cenaDo ? `${d.cenaOd.toLocaleString("cs-CZ")} Kč`
                    : `${d.cenaOd.toLocaleString("cs-CZ")}–${(d.cenaDo ?? 0).toLocaleString("cs-CZ")} Kč`}
                  <span className="ml-1 text-[10px] font-normal text-zinc-500">naše</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Panel s rozměry — otevře se pod mřížkou po kliknutí na kartu. */}
      {dezeny?.filter((d) => otevrene.has(`${d.brand}|${d.pattern}`)).map((d) => (
        <section key={`p-${d.brand}|${d.pattern}`} className="rounded-2xl border border-ktm-orange bg-white">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-100">
            {d.imgUrl && <img src={d.imgUrl} alt="" className="h-10 w-10 rounded object-contain" />}
            <div>
              <div className="font-medium">{d.brand} {d.pattern}</div>
              <div className="text-xs text-zinc-500">{d.pocetRozmeru} rozměrů · {d.kategorie}</div>
            </div>
            <Button size="sm" variant="ghost" className="ml-auto"
                    onClick={() => prepni(`${d.brand}|${d.pattern}`)}>Zavřít</Button>
          </div>
          <div className="overflow-x-auto px-4 pb-3">
            <table className="w-full min-w-[54rem]">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-zinc-400">
                  <th className="py-2 pr-3 text-left">Rozměr</th>
                  <th className="py-2 pr-3 text-left">Kód výrobce</th>
                  <th className="py-2 pr-3 text-right">Nákupka</th>
                  <th className="py-2 pr-3 text-right">Dodavatel prodává</th>
                  <th className="py-2 pr-3 text-right">Naše cena</th>
                  <th className="py-2 pr-3 text-right">vs dodavatel</th>
                  <th className="py-2 pr-3 text-left">Sklad u nich</th>
                  <th className="py-2 pr-3 text-left">Stav</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {d.rozmery.map((r) => (
                  <RadekRozmeru key={r.azpneuId} r={r} onZmena={() => void nacti()} />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
